import { IRequestContext } from '@msdyn365-commerce/core-internal';
import { ITelemetry } from '@msdyn365-commerce/telemetry-internal';
import { useCallback, useEffect, useState } from 'react';
import { ProductRecommendationTypes, RelevanceModifier } from '../services/RecommendationService';
import { useRecommendationService } from './serviceHooks';
import { DisplayedAtLocation, MappedProduct } from '../types/mapped';

const DEFAULT_PRODUCT_COUNT = 6;
const MAX_PRODUCT_COUNT = 20;

interface UseProductRecommendationOptions {
    productCount: number;
    recommendationType: ProductRecommendationTypes;
    isEnabled: boolean;
    displayLocation: DisplayedAtLocation;
    categoryId?: string;
    masterProductItemId?: string;
    relevanceModifier?: RelevanceModifier;
    relevanceModifierStrength?: number;
    request: IRequestContext;
    telemetry: ITelemetry;
}

export const useProductRecommendations = ({
    isEnabled,
    request,
    telemetry,
    productCount,
    displayLocation,
    recommendationType,
    categoryId,
    masterProductItemId,
    relevanceModifier,
    relevanceModifierStrength
}: UseProductRecommendationOptions) => {
    const [products, setProducts] = useState<MappedProduct[]>([]);
    const [isLoading, setIsLoading] = useState(isEnabled);

    const relewiseRecommendationService = useRecommendationService(request, telemetry);

    const loadRecommendations = useCallback(async () => {
        if (!isEnabled) {
            return;
        }
        setIsLoading(true);

        const numberOfProducts = Math.min(productCount ?? DEFAULT_PRODUCT_COUNT, MAX_PRODUCT_COUNT);
        const result = await relewiseRecommendationService.getProductRecommendations({
            recommendationType,
            displayLocation,
            categoryId,
            masterProductItemId,
            relevanceModifier,
            relevanceModifierStrength,
            count: numberOfProducts
        });

        setProducts(result ?? []);

        setIsLoading(false);
    }, [
        recommendationType,
        displayLocation,
        categoryId,
        masterProductItemId,
        relevanceModifier,
        relevanceModifierStrength,
        productCount,
        isEnabled,
        relewiseRecommendationService
    ]);

    useEffect(() => {
        loadRecommendations();
    }, [isEnabled, loadRecommendations]);

    return { products, isLoading };
};
